import React, { useEffect, useState, useCallback } from 'react';
import { Edit, SimpleForm, useRecordContext, useNotify, useTranslate } from 'react-admin';
import {
  Autocomplete, Button, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import { LinkOffRounded as UnassignIcon } from '@mui/icons-material';
import { httpClient } from '@/utils/httpClient';
import { FormPageTitle, FormSection, FormToolbar } from '@/components/shell';
import { CostCenterFields } from './CostCenterFields';

const BACK_TO = '/billing?tab=cost-centers';

/** One account assigned to the cost center; `id` is the assignment's id. */
interface AssignedAccount {
  id: number;
  accountName: string;
}

interface AccountOption {
  id: string;
  accountResponse?: { accountName?: string; accountStatus?: string };
}

const AssignedAccountsSection = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const t = useTranslate();
  const [accounts, setAccounts] = useState<AssignedAccount[]>([]);
  const [allAccounts, setAllAccounts] = useState<AccountOption[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<AccountOption | null>(null);

  const loadAccounts = useCallback(async () => {
    if (!record?.id) return;
    try {
      const r = await httpClient.get<AssignedAccount[]>(`/admin/cost-center/GetCostCenterAccounts/${record.id}`);
      setAccounts(r.data);
    } catch { /* the list stays as it was */ }
  }, [record?.id]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  useEffect(() => {
    const p = new URLSearchParams({ draw: '1', length: '1000', start: '0', search: '', column: '', orderBy: 'asc' });
    httpClient.get<{ data?: AccountOption[] }>(`/admin/account/GetAccounts?${p}`).then((r) => {
      setAllAccounts((r.data.data || []).filter((a) => a.accountResponse?.accountStatus === 'Active'));
    });
  }, []);

  const assignAccount = async () => {
    if (!selectedAccount) return;
    try {
      await httpClient.post('/admin/cost-center/AssignAccount', {
        costCenterId: record?.id,
        accountId: selectedAccount.id,
      });
      notify('screens.costCenters.assigned');
      setSelectedAccount(null);
      loadAccounts();
    } catch { notify('screens.costCenters.assignFailed', { type: 'error' }); }
  };

  const unassignAccount = async (mappingId: number) => {
    try {
      await httpClient.delete(`/admin/cost-center/UnassignAccount/${mappingId}`);
      notify('screens.costCenters.unassigned');
      loadAccounts();
    } catch { notify('screens.costCenters.unassignFailed', { type: 'error' }); }
  };

  if (!record?.id) return null;

  return (
    <FormSection title={t('screens.costCenters.accountsTitle')} description={t('screens.costCenters.accountsHint')}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <Autocomplete
          options={allAccounts}
          getOptionLabel={(o) => o.accountResponse?.accountName || ''}
          value={selectedAccount}
          onChange={(_, v) => setSelectedAccount(v)}
          renderInput={(params) => <TextField {...params} label={t('screens.costCenters.selectAccount')} />}
          sx={{ flex: 1 }}
        />
        <Button variant="outlined" onClick={assignAccount} disabled={!selectedAccount}>
          {t('screens.costCenters.assign')}
        </Button>
      </Stack>
      {accounts.length > 0 ? (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('screens.costCenters.account')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {accounts.map((acc) => {
              const label = t('screens.costCenters.unassign', { name: acc.accountName });
              return (
                <TableRow key={acc.id}>
                  <TableCell>{acc.accountName}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={label}>
                      <IconButton onClick={() => unassignAccount(acc.id)} aria-label={label}>
                        <UnassignIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary">{t('screens.costCenters.noAccounts')}</Typography>
      )}
    </FormSection>
  );
};

export const CostCenterEdit = () => (
  <Edit mutationMode="pessimistic" redirect={BACK_TO} actions={<FormPageTitle mode="edit" />}>
    <SimpleForm toolbar={<FormToolbar backTo={BACK_TO} showDelete />}>
      <CostCenterFields />
      <AssignedAccountsSection />
    </SimpleForm>
  </Edit>
);
